import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import internalShipmentService, {
  InternalShipment,
  InternalShipmentFilters,
  InternalShipmentPayload,
} from '../services/internalShipmentService';

export const useInternalShipments = (filters: InternalShipmentFilters = {}) => {
  const query = useQuery({
    queryKey: ['internal-shipments', filters],
    queryFn: () => internalShipmentService.getInternalShipments(filters),
  });

  return {
    ...query,
    shipments: query.data?.data || [],
    pagination: query.data?.meta,
  };
};

export const useInternalShipment = (id?: string | number) => {
  const query = useQuery({
    queryKey: ['internal-shipment', id],
    queryFn: () => internalShipmentService.getInternalShipment(id!),
    enabled: !!id,
  });

  return {
    ...query,
    shipment: query.data?.data,
  };
};

export const useInternalShipmentMutations = () => {
  const queryClient = useQueryClient();

  const createShipment = useMutation({
    mutationFn: (payload: InternalShipmentPayload) => internalShipmentService.createInternalShipment(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['internal-shipments'] });
    },
  });

  const updateShipment = useMutation({
    mutationFn: ({ id, payload }: { id: string | number; payload: InternalShipmentPayload }) =>
      internalShipmentService.updateInternalShipment(id, payload),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['internal-shipments'] });
      queryClient.invalidateQueries({ queryKey: ['internal-shipment', response.data.id] });
    },
  });

  const cancelShipment = useMutation({
    mutationFn: (id: string | number) => internalShipmentService.cancelInternalShipment(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['internal-shipments'] });
      queryClient.invalidateQueries({ queryKey: ['internal-shipment', response.data.id] });
    },
  });

  const quoteShipment = useMutation({
    mutationFn: (payload: { state_id: number; weightKg: number; currency?: string }) =>
      internalShipmentService.quoteInternalShipment(payload),
  });

  return {
    createShipment,
    updateShipment,
    cancelShipment,
    quoteShipment,
  };
};

export const downloadShipmentPdf = async (shipment: InternalShipment, isAdmin = false) => {
  const blob = isAdmin
    ? await internalShipmentService.downloadAdminInternalShipmentPdf(shipment.id)
    : await internalShipmentService.downloadInternalShipmentPdf(shipment.id);

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${shipment.shipmentNumber || `internal-shipment-${shipment.id}`}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
